import { Form, FormGroup } from '@wyz/ui';
import { FieldErrors } from 'react-hook-form';
import { Trans } from '~/common/context';
import { IS2SBusinessRule } from '~/connect/types';

type ParameterDefinition = IS2SBusinessRule['parameterDefinitions'][number];

type EditableProps = {
  mode: 'editable';
  definitions: ParameterDefinition[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  ruleErrors?: FieldErrors<Record<string, string>> | null;
};

type ReadonlyProps = {
  mode: 'readonly';
  definitions: ParameterDefinition[];
  values: Record<string, string>;
  ruleErrors?: FieldErrors<Record<string, string>> | null;
};

type Props = EditableProps | ReadonlyProps;

export function BusinessRuleParameters(props: Props) {
  const { mode, definitions, values } = props;
  const ruleErrors = 'ruleErrors' in props ? props.ruleErrors : undefined;
  if (definitions.length === 0) return null;

  if (mode === 'readonly') {
    return (
      <div className='d-flex flex-column gap-2'>
        {definitions.map((def) => {
          const isNumeric = def.type === 'int' || def.type === 'float';
          return (
            <FormGroup key={def.key}>
              <Form.Label className='mb-0 text-nowrap required'>
                <strong>
                  <Trans i18nKey={def.i18nKey} />
                </strong>
              </Form.Label>
              <Form.Control
                type={isNumeric ? 'number' : 'text'}
                value={values[def.key] ?? ''}
                disabled
                style={{ maxWidth: '120px' }}
                isInvalid={!!ruleErrors?.[def.key]?.message}
              />
              {ruleErrors?.[def.key]?.message && (
                <Form.Control.Feedback type='invalid'>
                  {ruleErrors[def.key]?.message}
                </Form.Control.Feedback>
              )}
            </FormGroup>
          );
        })}
      </div>
    );
  }

  return (
    <div className='d-flex flex-column gap-2'>
      {definitions.map((def) => {
        const isNumeric = def.type === 'int' || def.type === 'float';
        const isRequired = def.constraints.some((c) => c.type === 'NOT_BLANK');
        return (
          <FormGroup key={def.key}>
            <Form.Label
              className={`mb-0 text-nowrap ${isRequired ? 'required' : ''}`}
            >
              <strong>
                <Trans i18nKey={def.i18nKey} />
              </strong>
            </Form.Label>
            <Form.Control
              type={isNumeric ? 'number' : 'text'}
              value={values[def.key] ?? ''}
              onChange={({ target }) => props.onChange(def.key, target.value)}
              isInvalid={!!ruleErrors?.[def.key]?.message}
              style={{ maxWidth: '120px' }}
            />
            {ruleErrors?.[def.key]?.message && (
              <Form.Control.Feedback type='invalid'>
                {ruleErrors[def.key]?.message}
              </Form.Control.Feedback>
            )}
          </FormGroup>
        );
      })}
    </div>
  );
}
